import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Layout } from "@/components/layout/Layout";
import SEO from "@/components/SEO.jsx";
import { SEO_KEYWORDS } from "@/seo/seoData";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ArrowRight,
  Globe,
  Code,
  Smartphone,
  Zap,
  Shield,
  TrendingUp,
  CheckCircle,
  ChevronDown,
  Layers,
} from "lucide-react";

const capabilities = [
  {
    icon: Code,
    title: "Custom Web Applications",
    description:
      "Purpose-built digital tools designed around specific business requirements.",
  },
  {
    icon: Layers,
    title: "Admin Dashboards",
    description:
      "Internal dashboards that help teams manage listings, data, updates, and workflows more efficiently.",
  },
  {
    icon: TrendingUp,
    title: "Custom Calculators",
    description:
      "Interactive tools that help users or teams calculate values, estimates, grants, or other key inputs more clearly.",
  },
  {
    icon: Shield,
    title: "Client Portals",
    description:
      "Secure client-facing tools that improve access, communication, and account functionality.",
  },
  {
    icon: Zap,
    title: "Workflow Tools",
    description:
      "Custom systems that reduce manual tasks and improve operational flow.",
  },
  {
    icon: Smartphone,
    title: "Integrated Solutions",
    description:
      "Tools that support your wider website, content, SEO, or business systems where needed.",
  },
];

const offerings = [
  {
    title: "Internal Business Tools",
    description:
      "Systems built for internal use to improve workflow, accuracy, and speed.",
  },
  {
    title: "Industry-Specific Calculators",
    description:
      "Custom calculators built around specific use cases, user needs, or regulated information.",
  },
  {
    title: "Inventory & Data Management Tools",
    description:
      "Tools that help businesses manage information, listings, and updates more efficiently.",
  },
  {
    title: "Client-Facing Platforms",
    description:
      "Digital tools that improve the way clients interact with your business online.",
  },
];

const deliverables = [
  "Custom planning around your workflow",
  "Functional UI for real use cases",
  "Clean and scalable development",
  "Role-based access or admin controls where needed",
  "Testing before launch",
  "Launch support and follow-up fixes",
  "Documentation or handoff guidance where needed",
  "Room for future improvements and expansion",
];

const processSteps = [
  {
    step: "01",
    title: "Discovery",
    description:
      "We learn about your workflow, the current bottleneck, and what the tool needs to improve.",
  },
  {
    step: "02",
    title: "Planning",
    description:
      "We define the tool structure, required functionality, user roles, and practical priorities.",
  },
  {
    step: "03",
    title: "Design",
    description:
      "We design interfaces around clarity, ease of use, and the tasks your users need to complete.",
  },
  {
    step: "04",
    title: "Development",
    description:
      "We build the tool with the right functionality, logic, and structure for your business.",
  },
  {
    step: "05",
    title: "Testing",
    description:
      "We test usability, core functions, and edge cases before launch.",
  },
  {
    step: "06",
    title: "Launch & Support",
    description:
      "We launch cleanly and continue with fixes, updates, and improvements as needed.",
  },
];

const faqs = [
  {
    question: "What is a custom web application?",
    answer: (
      <>
        A custom web application is a digital tool built around a specific
        business need, workflow, or user action instead of using a generic
        off-the-shelf platform.
      </>
    ),
    schemaAnswer:
      "A custom web application is a digital tool built around a specific business need, workflow, or user action instead of using a generic off-the-shelf platform.",
  },
  {
    question: "How do I know if I need a custom tool?",
    answer: (
      <>
        If your team is relying on manual work, workarounds, spreadsheets, or
        disconnected systems, a custom tool may help simplify the process.
      </>
    ),
    schemaAnswer:
      "If your team is relying on manual work, workarounds, spreadsheets, or disconnected systems, a custom tool may help simplify the process.",
  },
  {
    question: "Can you build dashboards or admin systems?",
    answer: (
      <>
        Yes. We build internal dashboards, admin panels, and workflow tools
        that help businesses manage data, listings, content, or operations more
        efficiently.
      </>
    ),
    schemaAnswer:
      "Yes. We build internal dashboards, admin panels, and workflow tools that help businesses manage data, listings, content, or operations more efficiently.",
  },
  {
    question: "Can custom tools work with my website?",
    answer: (
      <>
        Yes. Custom tools can be planned to work alongside your{" "}
        <Link
          to="/services/website-development"
          className="text-primary hover:underline"
        >
          website
        </Link>
        , landing pages, or digital systems depending on the project.
      </>
    ),
    schemaAnswer:
      "Yes. Custom tools can be planned to work alongside your website, landing pages, or digital systems depending on the project.",
  },
  {
    question: "Do you also support the tool after launch?",
    answer: (
      <>
        Yes. We can continue with{" "}
        <Link
          to="/services/maintenance-support"
          className="text-primary hover:underline"
        >
          maintenance, fixes, improvements, and future updates
        </Link>{" "}
        after launch.
      </>
    ),
    schemaAnswer:
      "Yes. We can continue with maintenance, fixes, improvements, and future updates after launch.",
  },
  {
    question: "Can you build custom calculators?",
    answer: (
      <>
        Yes. We build custom calculators for industry-specific use cases where
        users or teams need clear, fast, and repeatable calculations.
      </>
    ),
    schemaAnswer:
      "Yes. We build custom calculators for industry-specific use cases where users or teams need clear, fast, and repeatable calculations.",
  },
];

const whyChooseUs = [
  {
    title: "Problem-First Approach",
    description:
      "We start by understanding what the business needs to fix, improve, or simplify before defining the build.",
  },
  {
    title: "Practical, Not Overbuilt",
    description:
      "We focus on useful functionality, clean workflows, and tools that make sense for the business.",
  },
  {
    title: "Connected Thinking",
    description:
      "Custom tools are planned to work alongside your website, content, and broader digital setup.",
  },
  {
    title: "Support Beyond Launch",
    description:
      "We continue with updates, fixes, and improvements after the tool goes live.",
  },
];

export default function WebsiteDevelopmentPage() {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <Layout>
      <SEO
        title="Custom Web Application Development Company in Vancouver"
        description="We provide Custom Web Application Development that is suitable for your business. Get a Bespoke solution today."
        ogTitle="Custom Web Application Development Company in Vancouver"
        ogDescription="We provide Custom Web Application Development that is suitable for your business. Get a Bespoke solution today."
        keywords={SEO_KEYWORDS.customToolCreation}
      />

      {/* Hero Section */}
      <section className="pt-24 sm:pt-28 lg:pt-32 pb-16 sm:pb-18 lg:pb-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-foreground via-foreground/95 to-foreground/90" />
        <div className="absolute inset-0">
          <div className="absolute top-16 sm:top-20 right-6 sm:right-20 w-72 sm:w-96 h-72 sm:h-96 bg-primary/20 rounded-full blur-3xl" />
          <div className="absolute bottom-16 sm:bottom-20 left-6 sm:left-20 w-56 sm:w-72 h-56 sm:h-72 bg-secondary/20 rounded-full blur-3xl" />
        </div>

        <div className="container-custom relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Badge className="mb-6 bg-primary/20 text-primary-foreground border-none">
                <Globe className="h-4 w-4 mr-2" />
                Our Services
              </Badge>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold text-background mb-5 sm:mb-6 leading-tight"
            >
              Custom Web Application Development in{" "}
              <span className="gradient-text">Vancouver</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-lg sm:text-xl text-background/70 mb-8 max-w-2xl mx-auto leading-relaxed"
            >
              We build custom web applications, dashboards, calculators, and
              internal tools that help businesses solve specific problems, save
              time, and work more efficiently.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-center justify-center"
            >
              <Button
                variant="gradient"
                size="lg"
                asChild
                className="group w-full sm:w-auto justify-center"
              >
                <Link
                  to="/contact"
                  className="flex items-center justify-center gap-2"
                >
                  Book a Free Consultation
                  <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>

              <Button
                variant="glass"
                size="lg"
                asChild
                className="w-full sm:w-auto justify-center text-white bg-white/10 border border-white/20 hover:shadow-[0_8px_30px_rgba(255,255,255,0.12)] hover:-translate-y-[1px] transition-all duration-300"
              >
                <Link to="/contact">View Our Work</Link>
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Value Section */}
      <section className="pt-12 sm:pt-14 lg:pt-20 pb-6 sm:pb-8 lg:pb-10 bg-background relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-24 left-10 w-72 h-72 bg-primary/5 rounded-full blur-3xl" />
          <div className="absolute -bottom-24 right-10 w-72 h-72 bg-secondary/5 rounded-full blur-3xl" />
        </div>

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-5xl mx-auto text-center"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-4">
              Build Tools That Solve Real{" "}
              <span className="gradient-text">Business Problems</span>
            </h2>

            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-3xl mx-auto mb-6">
              Sometimes a business problem cannot be solved with an
              off-the-shelf platform. That is where custom tool creation makes
              sense. We build digital tools around how your business actually
              works, so your team can save time, reduce manual work, and operate
              more efficiently.
            </p>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-card/60 backdrop-blur rounded-2xl border border-border shadow-soft p-6 sm:p-8"
            >
              <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-3xl mx-auto mb-6">
                From calculators and{" "}
                <Link
                  to="/services/custom-tool-creation"
                  className="text-primary hover:underline"
                >
                  dashboards
                </Link>{" "}
                to admin panels and client-facing tools, we focus on practical
                solutions that support real workflows.
              </p>

              <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
                {[
                  {
                    title: "Built for your workflow",
                    desc: "Your tool should fit the way your team already works, not force extra steps.",
                  },
                  {
                    title: "Less manual work",
                    desc: "Custom tools can reduce repetitive tasks and improve day-to-day efficiency.",
                  },
                  {
                    title: "Better control",
                    desc: "Dashboards and admin systems give your team more control without relying on developers for small updates.",
                  },
                  {
                    title: "Scalable functionality",
                    desc: "A custom web application can grow with the business as needs become more complex.",
                  },
                  {
                    title: "Practical business value",
                    desc: "The right tool should make work easier, faster, and more reliable.",
                  },
                ].map((item, i) => (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.45, delay: 0.15 + i * 0.05 }}
                    className="rounded-xl border border-border bg-background/70 p-4 text-center hover:shadow-medium transition-shadow"
                  >
                    <div className="text-sm font-semibold text-foreground">
                      {item.title}
                    </div>
                    <div className="mt-1 text-xs sm:text-sm text-muted-foreground leading-relaxed">
                      {item.title === "Better control" ? (
                        <>
                          Dashboards and{" "}
                          <Link
                            to="/services/custom-tool-creation"
                            className="text-primary hover:underline"
                          >
                            admin systems
                          </Link>{" "}
                          give your team more control without relying on
                          developers for small updates.
                        </>
                      ) : (
                        item.desc
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="pt-12 sm:pt-14 lg:pt-14 pb-6 sm:pb-8 lg:pb-10 bg-background relative overflow-hidden">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto mb-10"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground">
              Why Choose Square Trigger for Custom Tool Creation
            </h2>

            <p className="text-base sm:text-lg text-muted-foreground mt-4 leading-relaxed">
              We do not build custom tools just because they sound impressive.
              We build them when they solve a real business problem and improve
              the way the work gets done.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyChooseUs.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08 }}
                className="p-6 bg-card rounded-2xl border border-border shadow-soft hover:shadow-medium transition-shadow text-center sm:text-left flex flex-col items-center sm:items-start"
              >
                <div className="w-10 h-10 rounded-xl gradient-primary-soft flex items-center justify-center mb-4">
                  <div className="w-2.5 h-2.5 rounded-full bg-primary" />
                </div>

                <h3 className="font-semibold text-foreground mb-2">
                  {item.title}
                </h3>

                <p className="text-sm text-muted-foreground leading-relaxed">
                  {item.title === "Connected Thinking" ? (
                    <>
                      Custom tools are planned to work alongside your{" "}
                      <Link
                        to="/services/website-development"
                        className="text-primary hover:underline"
                      >
                        website
                      </Link>
                      , content, and broader digital setup.
                    </>
                  ) : item.title === "Support Beyond Launch" ? (
                    <>
                      We continue with{" "}
                      <Link
                        to="/services/maintenance-support"
                        className="text-primary hover:underline"
                      >
                        updates, fixes, and improvements
                      </Link>{" "}
                      after the tool goes live.
                    </>
                  ) : (
                    item.description
                  )}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="pt-12 sm:pt-14 lg:pt-16 pb-6 sm:pb-8 lg:pb-10 bg-background relative overflow-hidden">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto mb-12 sm:mb-14"
          >
            <span className="text-sm font-medium text-primary uppercase tracking-wider">
              Capabilities
            </span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mt-3">
              What We <span className="gradient-text">Build</span>
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {capabilities.map((capability, index) => {
              const Icon = capability.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.07 }}
                  className="h-full"
                >
                  <Card className="p-6 h-full border-border hover:border-primary/30 transition-all duration-300 card-hover text-center sm:text-left flex flex-col items-center sm:items-start">
                    <div className="w-12 h-12 rounded-xl gradient-primary flex items-center justify-center mb-4">
                      <Icon className="h-6 w-6 text-primary-foreground" />
                    </div>

                    <h3 className="text-lg sm:text-xl font-display font-semibold text-foreground mb-2">
                      {capability.title}
                    </h3>

                    <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                      {capability.title === "Integrated Solutions" ? (
                        <>
                          Tools that support your wider{" "}
                          <Link
                            to="/services/website-development"
                            className="text-primary hover:underline"
                          >
                            website
                          </Link>
                          , content,{" "}
                          <Link
                            to="/services/seo-services"
                            className="text-primary hover:underline"
                          >
                            SEO
                          </Link>
                          , or business systems where needed.
                        </>
                      ) : (
                        capability.description
                      )}
                    </p>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="section-padding bg-foreground text-background relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-10 left-10 w-80 h-80 bg-primary/10 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-80 h-80 bg-secondary/10 rounded-full blur-3xl" />
        </div>

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto mb-12 sm:mb-16"
          >
            <span className="text-sm font-medium text-secondary uppercase tracking-wider">
              Solutions
            </span>
            <h2 className="text-3xl md:text-4xl font-display font-bold mt-3">
              Custom Builds for Different{" "}
              <span className="gradient-text">Business Needs</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {offerings.map((offering, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                className="bg-background/5 rounded-2xl p-6 sm:p-8 border border-background/10 hover:border-primary/30 transition-colors text-center sm:text-left flex flex-col items-center sm:items-start"
              >
                <h3 className="text-xl sm:text-2xl font-display font-bold mb-3">
                  {offering.title === "Client-Facing Platforms" ? (
                    <Link
                      to="/services/website-development"
                      className="hover:underline"
                    >
                      {offering.title}
                    </Link>
                  ) : (
                    offering.title
                  )}
                </h3>

                <p className="text-background/70 leading-relaxed">
                  {offering.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Deliverables */}
      <section className="pt-12 sm:pt-14 lg:pt-16 pb-6 sm:pb-8 lg:pb-10 bg-background relative overflow-hidden">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="text-center lg:text-left"
            >
              <span className="text-sm font-medium text-primary uppercase tracking-wider">
                Deliverables
              </span>

              <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mt-3 mb-5">
                What You&apos;ll <span className="gradient-text">Receive</span>
              </h2>

              <p className="text-base sm:text-lg text-muted-foreground mb-8 leading-relaxed max-w-xl mx-auto lg:mx-0">
                Every custom tool creation project is scoped around the actual
                business need, with a focus on usability, performance, and
                long-term value.
              </p>

              <Button
                variant="gradient"
                size="lg"
                asChild
                className="group w-full sm:w-auto justify-center"
              >
                <Link
                  to="/contact"
                  className="flex items-center justify-center gap-2"
                >
                  Book a Free Consultation
                  <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="bg-card rounded-2xl p-6 sm:p-8 border border-border shadow-soft text-center sm:text-left"
            >
              <ul className="inline-grid sm:grid space-y-4 text-left">
                {deliverables.map((item, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: 14 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    className="flex items-start gap-3 w-fit"
                  >
                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-[2px]" />
                    <span className="text-sm sm:text-base text-foreground/90 leading-relaxed">
                      {item === "Launch support and follow-up fixes" ? (
                        <>
                          <Link
                            to="/services/maintenance-support"
                            className="text-primary hover:underline"
                          >
                            Launch support
                          </Link>{" "}
                          and follow-up fixes
                        </>
                      ) : (
                        item
                      )}
                    </span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="pt-12 sm:pt-14 lg:pt-12 pb-6 sm:pb-8 lg:pb-10 bg-background relative overflow-hidden">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto mb-12 sm:mb-16"
          >
            <span className="text-sm font-medium text-primary uppercase tracking-wider">
              Our Process
            </span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground">
              How We Build <span className="gradient-text">Custom Tools</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {processSteps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                className="relative h-full"
              >
                <Card className="p-6 h-full border-border hover:border-primary/30 transition-colors card-hover text-center sm:text-left flex flex-col items-center sm:items-start">
                  <div className="w-full mb-4 flex justify-center">
                    <div className="text-3xl font-display font-bold gradient-text text-center">
                      {step.step}
                    </div>
                  </div>

                  <h3 className="text-lg sm:text-xl font-display font-semibold text-foreground mb-2">
                    {step.title}
                  </h3>
                  <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                    {step.title === "Launch & Support" ? (
                      <>
                        We launch cleanly and continue with{" "}
                        <Link
                          to="/services/maintenance-support"
                          className="text-primary hover:underline"
                        >
                          fixes, updates, and improvements
                        </Link>{" "}
                        as needed.
                      </>
                    ) : (
                      step.description
                    )}
                  </p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Mid CTA */}
      <section className="py-16 sm:py-20 gradient-primary relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-0 w-80 h-80 sm:w-96 sm:h-96 bg-background/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-80 h-80 sm:w-96 sm:h-96 bg-background/10 rounded-full blur-3xl" />
        </div>

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold text-primary-foreground mb-4 sm:mb-6">
              Need a Custom Tool Built Around Your Business?
            </h2>

            <p className="text-base sm:text-lg text-primary-foreground/80 mb-8 max-w-2xl mx-auto leading-relaxed">
              Let’s talk about the process you want to improve and whether a
              custom web application is the right solution.
            </p>

            <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
              <Button
                variant="premium"
                size="lg"
                asChild
                className="bg-background text-foreground hover:bg-background/90 w-full sm:w-auto justify-center"
              >
                <Link
                  to="/contact"
                  className="flex items-center justify-center gap-2"
                >
                  Book a Free Consultation
                  <ArrowRight className="h-5 w-5" />
                </Link>
              </Button>

              <Button
                variant="glass"
                size="lg"
                asChild
                className="w-full sm:w-auto justify-center text-white bg-white/10 border border-white/20 hover:shadow-[0_8px_30px_rgba(255,255,255,0.12)] hover:-translate-y-[1px] transition-all duration-300"
              >
                <Link to="/contact">View Our Work</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQs */}
      <section className="pt-12 sm:pt-14 lg:pt-16 pb-6 sm:pb-8 lg:pb-10 bg-background relative overflow-hidden">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto mb-10 sm:mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mt-3">
              Custom Tool Creation –{" "}
              <span className="gradient-text">Frequently Asked Questions</span>
            </h2>
          </motion.div>

          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={faq.question}
                className="relative z-10 bg-card rounded-xl border border-border overflow-hidden"
              >
                <button
                  type="button"
                  onPointerUp={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-5 sm:p-6 text-left hover:bg-accent/50 transition-colors pointer-events-auto touch-manipulation"
                >
                  <span className="font-semibold text-foreground pr-6">
                    {faq.question}
                  </span>
                  <ChevronDown
                    className={`h-5 w-5 text-muted-foreground transition-transform duration-300 flex-shrink-0 ${
                      openFaq === index ? "rotate-180" : ""
                    }`}
                  />
                </button>

                <div
                  className={`grid transition-[grid-template-rows] duration-300 ease-out ${
                    openFaq === index ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                  }`}
                >
                  <div className="overflow-hidden">
                    <div className="px-5 sm:px-6 pb-5 sm:pb-6 text-muted-foreground leading-relaxed">
                      {faq.answer}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: faqs.map((faq) => ({
                "@type": "Question",
                name: faq.question,
                acceptedAnswer: {
                  "@type": "Answer",
                  text: faq.schemaAnswer,
                },
              })),
            })}
          </script>
        </div>
      </section>

      {/* Final CTA */}
      <section className="pt-12 sm:pt-14 lg:pt-16 pb-6 sm:pb-8 lg:pb-10 bg-background relative overflow-hidden">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-4 sm:mb-6">
              Have a Process That Needs a Better Digital Solution?
            </h2>

            <p className="text-base sm:text-lg text-muted-foreground mb-8 leading-relaxed max-w-2xl mx-auto">
              Tell us what is slowing the business down, and we’ll help you
              figure out whether a custom tool is the right next step.
            </p>

            <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
              <Button
                variant="gradient"
                size="lg"
                asChild
                className="group w-full sm:w-auto justify-center"
              >
                <Link
                  to="/contact"
                  className="flex items-center justify-center gap-2"
                >
                  Book a Free Consultation
                  <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>

              <Button
                variant="outline"
                size="lg"
                asChild
                className="w-full sm:w-auto justify-center"
              >
                <Link
                  to="/contact"
                  className="flex items-center justify-center"
                >
                  Contact Us
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}